import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { IconType } from 'react-icons';
import { FaBrain } from 'react-icons/fa';
import { 
  FaCalendarAlt, 
  FaEnvelope, 
  FaShoppingCart, 
  FaUsers, 
  FaChartBar, 
  FaFileInvoiceDollar
} from 'react-icons/fa';
import './Projects.css';

interface Project {
  id: number;
  title: string;
  description: string;
  detailedDescription: string;
  icon: IconType;
  image: string;
  tags: string[];
  metrics: {
    timeSaved: string;
    efficiency: string;
    roi: string;
  };
  features: string[];
}

const Projects: React.FC = () => {
  // Number of complete rows to show initially and per "Load More"
  const INITIAL_ROWS = 2;
  const ROWS_PER_LOAD = 2;

  const [columns, setColumns] = useState(3);
  const [rowsShown, setRowsShown] = useState(INITIAL_ROWS);
  const [isLoading, setIsLoading] = useState(false);

  // Detect the number of grid columns based on viewport width.
  // Must stay in sync with the breakpoints in Projects.css.
  const getColumnsForWidth = useCallback((width: number): number => {
    if (width >= 1600) return 4;
    if (width >= 1200) return 3;
    if (width >= 768) return 2;
    return 1;
  }, []);

  useEffect(() => {
    const updateColumns = () => {
      setColumns(getColumnsForWidth(window.innerWidth));
    };
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, [getColumnsForWidth]);

  // Visible projects is always a multiple of the current column count,
  // so every row is fully filled regardless of screen size.
  const visibleProjects = Math.min(rowsShown * columns, 20);

  const projects: Project[] = [
    {
      id: 1,
      title: "Weekly Social Media Post Generator",
      description: "Modular n8n workflow that produces strategy-led weekly social posting plans with AI-generated captions and images.",
      detailedDescription: "I developed a modular automation workflow using n8n that generates a strategy-led weekly social media posting plan. The system produces one post per day per platform, complete with captions, visuals, and CTAs — all aligned with the brand's strategy. The workflow first creates a Brand Profile, including key elements such as target audience, value proposition, and three content pillars. Using this foundation, it then generates platform-specific post concepts, copy, and AI-generated images. All results are consolidated into a Google Sheet/Doc structured as: Day → Platform → Concept → Caption → Image Links → CTA — allowing for easy human review and approval before publishing.",
      icon: FaCalendarAlt,
      image: "/images/projects/project1.png",
      tags: ["n8n", "AI", "Social Media", "Content Strategy", "Google Sheets"],
      metrics: {
        timeSaved: "10 hours/week",
        efficiency: "90%",
        roi: "300%"
      },
      features: [
        "Strategy-first content planning with Brand Profile creation",
        "AI-powered platform-specific content generation",
        "Automated caption and image creation",
        "Google Docs/Sheets integration for review workflow",
        "Daily post scheduling across multiple platforms",
        "Content pillar-based concept development",
        "Human-reviewable approval process before publishing"
      ]
    },
    {
      id: 2,
      title: "AI-Powered CMO Agent",
      description: "Agentic workflow that emulates a Chief Marketing Officer's strategic research, analysis, and marketing intelligence gathering from a simple company URL.",
      detailedDescription: "I developed an agentic, modular workflow that emulates how a Chief Marketing Officer (CMO) would research, analyze, and strategize for a company. The system is designed to automatically collect, analyze, and structure marketing intelligence — transforming a simple company URL into a comprehensive strategic marketing report. The AI workflow autonomously gathers insights about: The company: products, value propositions, and brand tone; The market & competitors: positioning and differentiation; The audience: target segments and behavioral insights; Messaging & USPs: key selling points, voice, and brand guidelines. This project leverages n8n, LLM-based agents, and custom prompt chains to simulate the decision-making and analytical process of a real marketing executive. Tech Stack: n8n, OpenAI API, web scraping tools, and data structuring pipelines.",
      icon: FaShoppingCart,
      image: "/images/projects/project2.png",
      tags: ["n8n", "AI Agent", "Marketing Strategy", "OpenAI API", "Web Scraping"],
      metrics: {
        timeSaved: "12 hours/week",
        efficiency: "95%",
        roi: "350%"
      },
      features: [
        "Automated company intelligence gathering",
        "Competitor analysis and positioning research",
        "Target audience segmentation and insights",
        "Brand voice and messaging strategy development",
        "Comprehensive marketing report generation",
        "Web scraping and data analysis pipelines",
        "LLM-powered strategic decision making"
      ]
    },
    {
      id: 3,
      title: "N8N Youtube Automation",
      description: "Fully automated YouTube publishing system that streamlines video upload from idea to published video with AI-generated hooks and automated editing.",
      detailedDescription: "I developed a fully automated YouTube publishing system using n8n that streamlines the entire video upload process — from idea to published video. The workflow pulls video ideas and metadata (title, tags, description) from ClickUp, downloads both video and audio files from Google Drive, and then uses FFmpeg to merge the audio with the video. It also uses AI to generate a compelling hook for the YouTube video before automatically uploading it to YouTube via the YouTube Data API. This automation eliminates manual steps like editing, metadata entry, and uploading — allowing consistent, fast, and hands-free YouTube content publishing. Tech Stack: n8n, FFmpeg, YouTube Data API, ClickUp API, Google Drive API, OpenAI API.",
      icon: FaFileInvoiceDollar,
      image: "/images/projects/project3.png",
      tags: ["n8n", "YouTube Data API", "FFmpeg", "ClickUp API", "Google Drive API"],
      metrics: {
        timeSaved: "8 hours/week",
        efficiency: "92%",
        roi: "280%"
      },
      features: [
        "Automated video idea management from ClickUp",
        "Google Drive video and audio file downloading",
        "FFmpeg video and audio merging automation",
        "AI-generated compelling YouTube video hooks",
        "Automated YouTube metadata entry and tagging",
        "Direct YouTube publishing via Data API",
        "Hands-free content publishing workflow"
      ]
    },
    {
      id: 4,
      title: "Facebook Ad Creative Automation (ClickUp + Make.com)",
      description: "Advanced automation that streamlines uploading new ad creatives from ClickUp to Facebook Ads Manager using the Facebook Marketing API.",
      detailedDescription: "I built an advanced automation in Make.com that streamlines the process of uploading new ad creatives from ClickUp directly to Facebook Ads Manager using the Facebook Marketing API. The workflow triggers automatically when a custom checkbox is marked in ClickUp. It then: Pulls winning ad copy, headlines, descriptions, and URLs from a Google Sheet; Fetches the creative file link from Air Inc., downloads it, and prepares it for upload; Reads the file name to identify the relevant offer and use case, ensuring each creative is assigned to the correct Facebook ad campaign. This system eliminated repetitive manual steps, ensuring every tested creative is seamlessly published for live campaign testing. Tech Stack: Make.com, ClickUp API, Facebook Marketing API, Google Sheets API, Air Inc. integration.",
      icon: FaUsers,
      image: "/images/projects/project4.png",
      tags: ["Make.com", "ClickUp API", "Facebook Marketing API", "Google Sheets", "Ad Management"],
      metrics: {
        timeSaved: "9 hours/week",
        efficiency: "93%",
        roi: "290%"
      },
      features: [
        "Automatic ClickUp checkbox triggers",
        "Facebook Marketing API integration",
        "Google Sheets data synchronization",
        "Air Inc. file management and download",
        "Intelligent creative-to-campaign matching",
        "Automated file name parsing and routing",
        "Seamless creative publishing workflow"
      ]
    },
    {
      id: 5,
      title: "N8N Blog Automation",
      description: "Fully automated AI-driven blogging system that takes topic ideas, performs research, generates SEO-optimized content, and publishes directly to websites.",
      detailedDescription: "I developed a fully automated AI-driven blogging system using n8n that takes topic ideas from Google Sheets, performs automated research, generates high-quality SEO-optimized blog content, and publishes it directly to the website. The workflow mimics the process of a human content writer — researching each topic, structuring the article, writing engaging copy, and formatting it for web publication. It ensures consistency in tone, structure, and keyword optimization, making it ideal for scaling content marketing operations. Tech Stack: n8n, OpenAI API, Google Sheets API, WordPress API (or Webflow CMS API), and custom prompt engineering for AI content generation.",
      icon: FaChartBar,
      image: "/images/projects/project5.png",
      tags: ["n8n", "AI Content", "SEO Optimization", "WordPress API", "Google Sheets"],
      metrics: {
        timeSaved: "11 hours/week",
        efficiency: "92%",
        roi: "320%"
      },
      features: [
        "Automated topic research and analysis",
        "AI-powered content generation with SEO optimization",
        "Direct website publishing via CMS APIs",
        "Consistent tone and structure maintenance",
        "Custom prompt engineering for quality content",
        "Google Sheets integration for content planning",
        "Scalable content marketing operations"
      ]
    },
    {
      id: 6,
      title: "WhatsApp Lead Management Automation",
      description: "AI-powered WhatsApp lead management system that captures messages, classifies customer intent, logs details, and assigns teams for follow-up.",
      detailedDescription: "I created an AI-powered WhatsApp lead management system using n8n that captures incoming customer messages, classifies them intelligently, logs details, and assigns the right team for follow-up. When a message is received, the system uses an OpenAI-powered AI Agent to understand the customer's intent — such as renewals, cancellations, payment inquiries, or new membership requests. It then automatically: Logs the conversation in the relevant Google Sheet; Sends notifications to the appropriate Slack channel (Leads, Existing Customers, etc.); Replies to the customer via WhatsApp with a personalized message. This automation ensures no lead or customer inquiry is missed and provides instant, structured handling of incoming WhatsApp messages. Tech Stack: n8n, OpenAI API, Google Sheets API, Slack API, WhatsApp Business API.",
      icon: FaFileInvoiceDollar,
      image: "/images/projects/project6.png",
      tags: ["n8n", "WhatsApp Business API", "OpenAI API", "Slack API", "Lead Management"],
      metrics: {
        timeSaved: "10 hours/week",
        efficiency: "94%",
        roi: "300%"
      },
      features: [
        "AI-powered customer intent classification",
        "Automated conversation logging in Google Sheets",
        "Smart team assignment via Slack notifications",
        "Personalized WhatsApp auto-responses",
        "Real-time lead capture and processing",
        "Multi-channel integration and workflow management",
        "Structured handling of all customer inquiries"
      ]
    },
    {
      id: 7,
      title: "SEO Keyword Research & Report Automation",
      description: "AI-powered keyword research automation that analyzes websites and generates comprehensive SEO reports with organized keyword pillars and clusters.",
      detailedDescription: "I built an AI-powered keyword research automation using n8n that streamlines SEO analysis for websites. The workflow is triggered via webhook input, analyzes a given website's products and services, and then automatically generates a comprehensive keyword set tailored to its niche. The system uses AI-driven text analysis to identify relevant keyword Pillars and Clusters, ensuring an organized and strategy-focused output. Once complete, the workflow compiles the results into a professional report and delivers it directly via email. Tech Stack: n8n, OpenAI API, Google Sheets API, and SMTP automation for email delivery.",
      icon: FaEnvelope,
      image: "/images/projects/project7.png",
      tags: ["n8n", "SEO", "AI Analysis", "OpenAI API", "Google Sheets"],
      metrics: {
        timeSaved: "8 hours/week",
        efficiency: "85%",
        roi: "240%"
      },
      features: [
        "Webhook-triggered keyword analysis",
        "AI-powered website content analysis",
        "Automated keyword pillar and cluster organization",
        "Professional SEO report generation",
        "Direct email delivery of results",
        "OpenAI API integration for text analysis",
        "Google Sheets API for data compilation"
      ]
    },
    {
      id: 8,
      title: "AI Dental Receptionist Management Dashboard",
      description: "Web-based admin interface that lets clinic staff manage the AI receptionist's knowledge base — clinic info, hours, services, and policies — without any technical involvement.",
      detailedDescription: "I built a web-based administration dashboard that gives dental clinic staff full control over the AI receptionist's knowledge base without requiring any technical involvement. Administrators can log in and update clinic information, business hours, available services, pricing, and clinic policies through a clean form-driven interface. Every update is pushed into the AI knowledge base in real time, ensuring the AI assistant always answers patient questions accurately and with the latest information. The dashboard also includes role-based access, an audit log of changes, and a preview mode to test how the AI will respond before publishing updates live. Tech Stack: React, FastAPI, and a custom AI knowledge base layer.",
      icon: FaUsers,
      image: "/images/projects/project8.png",
      tags: ["AI Agent", "React", "FastAPI", "Knowledge Base", "Healthcare"],
      metrics: {
        timeSaved: "8 hours/week",
        efficiency: "92%",
        roi: "280%"
      },
      features: [
        "Form-driven knowledge base management for non-technical staff",
        "Real-time sync of updates to the AI receptionist",
        "Clinic info, hours, services, and policy management",
        "Role-based access control for administrators",
        "Audit log of all knowledge base changes",
        "Preview mode to test AI responses before publishing",
        "Clean, intuitive admin dashboard UI"
      ]
    },
    {
      id: 9,
      title: "AI Blog Generation & WordPress Publishing",
      description: "RSS-driven content pipeline that retrieves articles, enriches them with multiple LLMs, generates SEO-optimized blog posts, and publishes directly to WordPress.",
      detailedDescription: "I engineered an RSS-driven content pipeline that turns source articles into fully published, SEO-optimized blog posts with minimal manual intervention. The workflow retrieves articles from configured RSS feeds, processes and enriches the content using multiple LLMs in sequence, generates a structured SEO-optimized blog post, formats the output for web publication, publishes it directly to WordPress, and sends an email notification once publication succeeds. By chaining multiple LLMs, the pipeline separates research, drafting, and SEO refinement into distinct stages, producing higher-quality output than a single-pass generation. Tech Stack: Make, OpenAI, Anthropic Claude, WordPress, and Gmail.",
      icon: FaFileInvoiceDollar,
      image: "/images/projects/project9.png",
      tags: ["Make", "OpenAI", "Anthropic Claude", "WordPress", "Content Automation"],
      metrics: {
        timeSaved: "12 hours/week",
        efficiency: "94%",
        roi: "360%"
      },
      features: [
        "RSS-driven article retrieval and ingestion",
        "Multi-LLM content enrichment pipeline",
        "SEO-optimized blog post generation",
        "Automated formatting for web publication",
        "Direct WordPress publishing via API",
        "Email notifications on successful publication",
        "Staged research, drafting, and SEO refinement"
      ]
    },
    {
      id: 10,
      title: "AI Voice Transcription & Analysis Bot",
      description: "Telegram bot that accepts voice messages, transcribes them with Whisper, structures the content with AI, and logs everything to Google Sheets.",
      detailedDescription: "I built a Telegram bot that accepts voice messages and turns them into structured, actionable data. When a user sends a voice message, the bot downloads the audio file, processes it through OpenAI Whisper for transcription, passes the transcript through an LLM to structure and analyze the extracted information, generates a summary or response, and logs all processed data into Google Sheets. The bot handles multiple audio formats, manages long messages gracefully, and provides real-time status updates back to the user in Telegram. Tech Stack: Telegram Bot API, Make, OpenAI Whisper, and Google Sheets.",
      icon: FaEnvelope,
      image: "/images/projects/project10.png",
      tags: ["Telegram Bot", "Make", "OpenAI Whisper", "Google Sheets", "Voice AI"],
      metrics: {
        timeSaved: "6 hours/week",
        efficiency: "90%",
        roi: "240%"
      },
      features: [
        "Telegram voice message ingestion",
        "OpenAI Whisper-powered transcription",
        "AI structuring and analysis of transcript content",
        "Automated summary and response generation",
        "Google Sheets logging of all processed data",
        "Multi-format audio handling",
        "Real-time status updates to the user"
      ]
    },
    {
      id: 11,
      title: "AI-Based Resume Processing Pipeline",
      description: "Webhook-triggered pipeline that receives uploaded resumes, extracts structured data with OCR and AI, stores results in Google Sheets, and notifies Slack.",
      detailedDescription: "I built an AI-based resume processing pipeline that turns unstructured resume uploads into structured, searchable candidate data. A webhook receives uploaded resumes, extracts the downloadable files, and processes PDFs through OCR and AI extraction. The pipeline identifies personal information, work experience, education, and skills, stores the structured results in Google Sheets, and notifies the recruiting team in Slack at each stage of processing. Conditional routing handles extraction failures and different outcome scenarios, ensuring no resume is lost and edge cases are flagged for manual review. Tech Stack: Make, OpenAI, Google Sheets, Slack, and HTTP webhooks.",
      icon: FaFileInvoiceDollar,
      image: "/images/projects/project11.png",
      tags: ["Make", "OpenAI", "Google Sheets", "Slack", "OCR"],
      metrics: {
        timeSaved: "10 hours/week",
        efficiency: "93%",
        roi: "300%"
      },
      features: [
        "Webhook-triggered resume ingestion",
        "OCR and AI-based structured data extraction",
        "Personal info, experience, education, and skills parsing",
        "Google Sheets storage of structured results",
        "Slack notifications at each processing stage",
        "Conditional routing for failures and edge cases",
        "Manual review flagging for low-confidence extractions"
      ]
    },
    {
      id: 12,
      title: "Slack ↔ Shopify Order Management",
      description: "Workflow that turns Slack into an operational dashboard for Shopify orders — staff trigger actions, retrieve details, and track issues without leaving Slack.",
      detailedDescription: "I built a workflow that turns Slack into a command center for Shopify order management. Staff can trigger actions directly from Slack messages, retrieve full order details on demand, update Shopify order metadata, manage Slack reactions to indicate workflow status, and automatically handle order issue tracking — all without leaving the Slack interface. The workflow bridges Slack and Shopify APIs in real time, reducing context switching for operations teams and creating a single, auditable channel for order-related actions. Tech Stack: n8n, Slack API, and Shopify API.",
      icon: FaShoppingCart,
      image: "/images/projects/project12.png",
      tags: ["n8n", "Slack API", "Shopify API", "Order Management", "Operations"],
      metrics: {
        timeSaved: "7 hours/week",
        efficiency: "88%",
        roi: "220%"
      },
      features: [
        "Slack-triggered Shopify order actions",
        "On-demand order detail retrieval",
        "Shopify order metadata updates from Slack",
        "Slack reactions for workflow status tracking",
        "Automated order issue handling",
        "Single auditable channel for order operations",
        "Real-time Slack ↔ Shopify API bridging"
      ]
    },
    {
      id: 13,
      title: "Low Inventory Monitoring & Purchase Order System",
      description: "Scheduled workflow that consolidates Shopify inventory across locations, calculates reorder requirements, and sends summarized alerts to procurement.",
      detailedDescription: "I built a scheduled inventory monitoring workflow that gives procurement teams a consolidated, accurate view of stock across all locations. The workflow retrieves products, inventory levels, purchase orders, and transfers from Shopify through paginated API requests, consolidates all inventory sources, calculates true stock availability, determines reorder requirements based on configurable thresholds, formats clear alerts, and sends summarized notifications to the purchasing team in Slack. By running on a schedule, it ensures procurement always works from fresh data and can act on low-stock situations before they become stockouts. Tech Stack: n8n, Shopify API, and Slack.",
      icon: FaChartBar,
      image: "/images/projects/project13.png",
      tags: ["n8n", "Shopify API", "Slack", "Inventory Management", "Procurement"],
      metrics: {
        timeSaved: "9 hours/week",
        efficiency: "91%",
        roi: "260%"
      },
      features: [
        "Scheduled multi-source inventory retrieval",
        "Paginated Shopify API requests for full catalog coverage",
        "Consolidation of inventory, purchase orders, and transfers",
        "True stock availability calculation",
        "Configurable reorder threshold logic",
        "Formatted, summarized Slack alerts for procurement",
        "Stockout prevention through scheduled monitoring"
      ]
    },
    {
      id: 14,
      title: "Advanced Contact Routing & Outreach Engine",
      description: "Intelligent outreach engine that routes scheduled leads into multiple paths based on contact info, email availability, and project assignments.",
      detailedDescription: "I engineered an advanced contact routing and outreach engine that intelligently directs scheduled leads into the right outreach path based on available contact information, email availability, and project assignments. The workflow evaluates each lead's data profile and routes it into the appropriate branch — creating Gmail drafts with personalized messaging, updating CRM records to reflect outreach status, archiving historical data for completed leads, and removing processed leads from active queues. The engine supports multiple outreach strategies in parallel, ensuring every lead receives the right touch at the right time without manual triage. Tech Stack: n8n, Gmail, Google Sheets, and CRM APIs.",
      icon: FaUsers,
      image: "/images/projects/project14.png",
      tags: ["n8n", "Gmail", "Google Sheets", "CRM APIs", "Lead Outreach"],
      metrics: {
        timeSaved: "11 hours/week",
        efficiency: "92%",
        roi: "290%"
      },
      features: [
        "Multi-path lead routing based on contact data profile",
        "Email availability and project assignment evaluation",
        "Personalized Gmail draft generation",
        "CRM record updates reflecting outreach status",
        "Historical data archiving for completed leads",
        "Automatic removal of processed leads from active queues",
        "Parallel support for multiple outreach strategies"
      ]
    },
    {
      id: 15,
      title: "Reception Call Workflow",
      description: "Advanced receptionist automation that processes new entries, categorizes requests, routes them into call outcomes, and archives completed interactions.",
      detailedDescription: "I built an advanced receptionist automation that handles the full lifecycle of incoming call requests. The workflow processes new spreadsheet entries, categorizes each incoming request, and routes it into specialized call outcomes based on the request type. It generates CRM updates, creates follow-up actions for the relevant teams, and archives completed interactions for record-keeping. Multiple branches handle different appointment and reception scenarios automatically, ensuring every call request is triaged, actioned, and logged without manual handling. Tech Stack: Zapier, Google Sheets, CRM, and Gmail.",
      icon: FaCalendarAlt,
      image: "/images/projects/project15.png",
      tags: ["Zapier", "Google Sheets", "CRM", "Gmail", "Reception Automation"],
      metrics: {
        timeSaved: "8 hours/week",
        efficiency: "89%",
        roi: "230%"
      },
      features: [
        "New entry processing and request categorization",
        "Specialized call outcome routing",
        "Automated CRM updates",
        "Follow-up action creation for relevant teams",
        "Completed interaction archiving",
        "Multi-branch handling of appointment scenarios",
        "Full call request lifecycle automation"
      ]
    },
    {
      id: 16,
      title: "Multi-Stage Lead Nurturing & CRM Automation",
      description: "Zapier workflow that automates the entire lead follow-up process from Google Sheets — validating, categorizing, and routing leads through tailored email sequences.",
      detailedDescription: "I built a multi-stage lead nurturing workflow in Zapier that automates the entire lead follow-up process from Google Sheets. When a new lead is added, the data is validated and categorized before routing it through different email sequences based on available contact information and timing rules. Depending on whether an email exists, multiple contacts are available, or the lead falls into an early follow-up window, personalized emails are sent, Zoho CRM is updated, and processed leads are archived by moving them into historical Google Sheets. The workflow ensures every lead receives timely, relevant follow-up without manual list management. Tech Stack: Zapier, Google Sheets, Gmail, Zoho CRM, and AI by Zapier.",
      icon: FaUsers,
      image: "/images/projects/project16.png",
      tags: ["Zapier", "Google Sheets", "Gmail", "Zoho CRM", "Lead Nurturing"],
      metrics: {
        timeSaved: "10 hours/week",
        efficiency: "91%",
        roi: "270%"
      },
      features: [
        "Automated lead validation and categorization",
        "Routing into different email sequences by data profile",
        "Email existence and multi-contact branching",
        "Early follow-up window timing rules",
        "Personalized email sending via Gmail",
        "Zoho CRM updates reflecting outreach status",
        "Processed lead archiving into historical sheets"
      ]
    },
    {
      id: 17,
      title: "Intelligent Email Follow-up Manager",
      description: "Zapier automation that listens for new or updated Zoho CRM contacts and intelligently continues or initiates email conversations without duplicate outreach.",
      detailedDescription: "I built an intelligent email follow-up manager in Zapier that listens for new or updated contacts in Zoho CRM and determines whether emails already exist for either reception or direct contacts. Existing conversations are continued seamlessly, while missing conversations automatically generate new email drafts. The workflow intelligently separates different contact types and prevents duplicate outreach, ensuring no contact receives redundant messages and no conversation is dropped. This keeps follow-up cadences consistent across the entire contact database without manual tracking. Tech Stack: Zapier, Zoho CRM, and Gmail.",
      icon: FaEnvelope,
      image: "/images/projects/project17.png",
      tags: ["Zapier", "Zoho CRM", "Gmail", "Email Follow-up", "Contact Management"],
      metrics: {
        timeSaved: "7 hours/week",
        efficiency: "90%",
        roi: "240%"
      },
      features: [
        "Real-time listening for new and updated Zoho CRM contacts",
        "Reception vs. direct contact type separation",
        "Existing conversation detection and continuation",
        "Automatic new email draft generation for missing conversations",
        "Duplicate outreach prevention",
        "Consistent follow-up cadence across the contact database",
        "No manual conversation tracking required"
      ]
    },
    {
      id: 18,
      title: "Automated Email Campaign Pipeline",
      description: "n8n workflow that processes scheduled outreach campaigns — reading leads, validating contacts, drafting emails, updating CRM, and archiving completed leads.",
      detailedDescription: "I built an automated email campaign pipeline in n8n that processes scheduled outreach campaigns end-to-end. The workflow reads lead information from Google Sheets, validates project contacts, creates personalized Gmail drafts, updates CRM records to reflect outreach status, and archives completed leads. Multiple routing paths handle different contact scenarios — such as leads with single vs. multiple contacts or missing email addresses — while maintaining tight synchronization between spreadsheets and the CRM. The pipeline runs on a schedule, enabling consistent campaign execution without manual list handling. Tech Stack: n8n, Google Sheets, Gmail, and HTTP APIs.",
      icon: FaEnvelope,
      image: "/images/projects/project18.png",
      tags: ["n8n", "Google Sheets", "Gmail", "HTTP APIs", "Email Campaigns"],
      metrics: {
        timeSaved: "9 hours/week",
        efficiency: "92%",
        roi: "260%"
      },
      features: [
        "Scheduled campaign processing from Google Sheets",
        "Project contact validation",
        "Personalized Gmail draft creation",
        "CRM record updates for outreach status",
        "Completed lead archiving",
        "Multiple routing paths for different contact scenarios",
        "Spreadsheet and CRM synchronization"
      ]
    },
    {
      id: 19,
      title: "Scheduled Lead Outreach Automation",
      description: "Scheduled n8n workflow that retrieves pending leads, drafts personalized emails, updates CRM, and archives processed leads with separate branches for contact availability.",
      detailedDescription: "I built a scheduled lead outreach automation in n8n that retrieves pending leads, checks available project contacts, and processes each lead through the appropriate outreach path. The workflow creates personalized email drafts, updates CRM records to reflect outreach status, archives processed leads into historical sheets, and removes completed entries from active lead queues. Separate branches handle leads with and without available contact information, ensuring no lead is skipped and every record reaches a clean terminal state. The scheduled execution model keeps outreach cadences consistent without any manual triggering. Tech Stack: n8n, Gmail, Google Sheets, and CRM APIs.",
      icon: FaCalendarAlt,
      image: "/images/projects/project19.png",
      tags: ["n8n", "Gmail", "Google Sheets", "CRM APIs", "Lead Outreach"],
      metrics: {
        timeSaved: "8 hours/week",
        efficiency: "91%",
        roi: "250%"
      },
      features: [
        "Scheduled retrieval of pending leads",
        "Project contact availability checking",
        "Personalized email draft creation",
        "CRM record updates for outreach status",
        "Processed lead archiving into historical sheets",
        "Removal of completed entries from active queues",
        "Separate branches for leads with and without contacts"
      ]
    },
    {
      id: 20,
      title: "Bulk Lead Processing & Email Generation",
      description: "n8n workflow that processes large batches of CRM leads — looping through records, applying business rules, generating personalized emails, and logging activity.",
      detailedDescription: "I built a bulk lead processing workflow in n8n that safely handles high volumes of CRM leads in batches. The workflow loops through records, applies business rules to each lead, generates personalized email messages for different scenarios, updates CRM records, logs all activity into spreadsheets, and maintains workflow state using wait nodes and batching. The batching and wait-node design prevents API rate-limit issues and keeps the workflow resilient when processing thousands of records. This makes it possible to run large-scale outreach campaigns that would be impractical to manage manually. Tech Stack: n8n, CRM APIs, Gmail, Google Sheets, and JavaScript.",
      icon: FaChartBar,
      image: "/images/projects/project20.png",
      tags: ["n8n", "CRM APIs", "Gmail", "Google Sheets", "JavaScript"],
      metrics: {
        timeSaved: "12 hours/week",
        efficiency: "94%",
        roi: "310%"
      },
      features: [
        "Batched processing of large CRM lead volumes",
        "Record looping with business rule application",
        "Personalized email generation for multiple scenarios",
        "CRM record updates during processing",
        "Spreadsheet activity logging",
        "Workflow state management with wait nodes",
        "Rate-limit-safe batching for thousands of records"
      ]
    }
  ];

  const handleLoadMore = () => {
    setIsLoading(true);
    
    // Simulate loading delay for smooth UX
    setTimeout(() => {
      setRowsShown(prev => prev + ROWS_PER_LOAD);
      setIsLoading(false);
    }, 800);
  };

  const handleShowLess = () => {
    setIsLoading(true);
    
    // Simulate loading delay for smooth UX and scroll to top
    setTimeout(() => {
      setRowsShown(INITIAL_ROWS);
      setIsLoading(false);
      
      // Smooth scroll to projects section
      const projectsSection = document.querySelector('.projects');
      if (projectsSection) {
        projectsSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 600);
  };

  return (
    <div className="projects section">
      <div className="container">
        <motion.div
          className="projects-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title gradient-text">Selected Engagements</h2>
          <p className="section-subtitle">
            A portfolio of production AI agents and automation systems engineered to deliver measurable operational impact
          </p>
        </motion.div>

        <div className="projects-grid">
          {projects.slice(0, visibleProjects).map((project, index) => (
            <motion.div
              key={project.id}
              className="project-card card"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="project-image">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="project-thumbnail"
                  onError={(e) => {
                    // Fallback to gradient background with icon if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.classList.add('fallback-mode');
                    }
                  }}
                />
                <div className="project-overlay">
                  <div className="project-icon">
                    {project.tags.includes('n8n') ? (
                      React.createElement(FaBrain as any)
                    ) : (
                      React.createElement(project.icon as any)
                    )}
                  </div>
                  <div className="project-metrics">
                    <span className="metric">{project.metrics.timeSaved} saved</span>
                  </div>
                </div>
              </div>
              
              <div className="project-content">
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">{project.description}</p>
                
                <div className="project-tags">
                  {project.tags.map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
                

                
                <Link to={`/project/${project.id}`} className="project-link btn">
                  View Details
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {(visibleProjects < projects.length || rowsShown > INITIAL_ROWS) && (
          <motion.div
            className="load-more-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {visibleProjects < projects.length ? (
              <button
                className={`load-more-btn ${isLoading ? 'loading' : ''}`}
                onClick={handleLoadMore}
                disabled={isLoading}
              >
                <div className="btn-content">
                  {isLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      <span>Loading more projects...</span>
                    </>
                  ) : (
                    <>
                      {React.createElement(FaBrain as any)}
                      <span>Load More Projects</span>
                      <div className="btn-glow"></div>
                    </>
                  )}
                </div>
              </button>
            ) : (
              <button
                className={`load-more-btn show-less-btn ${isLoading ? 'loading' : ''}`}
                onClick={handleShowLess}
                disabled={isLoading}
              >
                <div className="btn-content">
                  {isLoading ? (
                    <>
                      <div className="loading-spinner"></div>
                      <span>Collapsing projects...</span>
                    </>
                  ) : (
                    <>
                      {React.createElement(FaBrain as any)}
                      <span>Show Less Projects</span>
                      <div className="btn-glow"></div>
                    </>
                  )}
                </div>
              </button>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Projects;
export type { Project };